import { Lock, PlayCircle, FileText } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";

interface Lesson {
  id: string;
  title: string;
  description: string | null;
  lesson_type: string;
  duration_minutes: number | null;
  is_preview: boolean;
  order_index: number;
}

interface Module {
  id: string;
  title: string;
  description: string | null;
  order_index: number;
  lessons: Lesson[];
}

interface ModuleAccordionProps {
  modules: Module[];
  isEnrolled: boolean;
}

export const ModuleAccordion = ({ modules, isEnrolled }: ModuleAccordionProps) => {
  const { t } = useLanguage();

  const getLessonIcon = (type: string) => {
    switch (type) {
      case "video":
        return <PlayCircle className="h-4 w-4" />;
      case "text":
        return <FileText className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const canAccessLesson = (lesson: Lesson) => {
    return isEnrolled || lesson.is_preview;
  };

  return (
    <Accordion type="multiple" className="w-full space-y-4">
      {modules.map((module, index) => {
        const totalLessons = module.lessons.length;
        const totalDuration = module.lessons.reduce(
          (sum, lesson) => sum + (lesson.duration_minutes || 0),
          0
        );

        return (
          <AccordionItem
            key={module.id}
            value={module.id}
            className="glass-card border-border/40 rounded-lg overflow-hidden"
          >
            <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-accent/5">
              <div className="flex items-start justify-between w-full text-left pr-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-sm font-semibold text-primary">
                      {index + 1}
                    </span>
                    <h3 className="text-lg font-semibold text-foreground">
                      {module.title}
                    </h3>
                  </div>
                  {module.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {module.description}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 ml-4">
                  <span className="text-xs text-muted-foreground">
                    {totalLessons} {t("courseDetail.lessons")}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {totalDuration} {t("courseDetail.minutes")}
                  </span>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <div className="space-y-2 mt-2">
                {module.lessons.map((lesson, lessonIndex) => {
                  const isAccessible = canAccessLesson(lesson);
                  return (
                    <div
                      key={lesson.id}
                      className={`flex items-center justify-between p-3 rounded-md border border-border/20 ${
                        isAccessible
                          ? "bg-background/40 hover:bg-accent/5"
                          : "bg-muted/20"
                      } transition-colors`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-xs text-muted-foreground w-6">
                          {lessonIndex + 1}
                        </span>
                        <div className="text-primary/80">
                          {getLessonIcon(lesson.lesson_type)}
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-foreground">
                            {lesson.title}
                          </h4>
                          {lesson.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {lesson.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {lesson.duration_minutes && (
                          <span className="text-xs text-muted-foreground">
                            {lesson.duration_minutes} {t("courseDetail.minutes")}
                          </span>
                        )}
                        {lesson.is_preview ? (
                          <Badge variant="secondary" className="text-xs">
                            {t("courseDetail.preview")}
                          </Badge>
                        ) : !isEnrolled ? (
                          <Lock className="h-4 w-4 text-muted-foreground" />
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};
